import React from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InventoryIcon from '@mui/icons-material/Inventory';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockButton from '../components/GuideMockButton';
import GuideMockContainer from '../components/GuideMockContainer';
import GuideMockListHeader from '../components/GuideMockListHeader';
import GuideMockSelect from '../components/GuideMockSelect';
import GuideMockTextField from '../components/GuideMockTextField';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideAssessmentRegister: React.FC = () => {
  return (
    <GuidePageLayout
      title="資産を登録する"
      description="新しいPC等の資産を台帳に登録します。"
      breadcrumbs={[{ label: '資産管理', path: '/guide/assessment/overview' }]}
    >
      <GuideSection title="前提">
        <GuideTable
          headers={['条件', '内容']}
          rows={[
            ['必要な権限', '管理者 / 管理部門'],
            ['ログイン状態', 'ログイン済みであること'],
          ]}
        />
      </GuideSection>

      <GuideSection title="手順">
        <GuideStepList
          steps={[
            { label: '資産管理画面を開く' },
            {
              label: '一覧パネル右上の「+」アイコンを押下して登録フォームを開く',
              content: (
                <GuideMockContainer label="リストヘッダー">
                  <GuideMockListHeader createLabel="資産新規登録" />
                </GuideMockContainer>
              ),
            },
            {
              label: '資産情報を入力する',
              description:
                'フォームタイトルは「資産登録」です。タイトル左の戻る矢印をクリックすると、登録を破棄して一覧に戻ります。',
              content: (
                <GuideMockContainer label="登録フォーム">
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ pointerEvents: 'none' }}
                    >
                      <IconButton size="small">
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="h6" fontWeight={700}>
                        資産登録
                      </Typography>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <GuideMockSelect
                          label="資産区分"
                          options={['PC', 'モニター', '周辺機器']}
                          required
                        />
                      </Grid>
                      <Grid size={6}>
                        <GuideMockTextField label="資産名" required />
                      </Grid>
                      <Grid size={6}>
                        <GuideMockSelect
                          label="利用者"
                          options={['ユーザーA', 'ユーザーB']}
                          required
                        />
                      </Grid>
                      <Grid size={6}>
                        <GuideMockSelect
                          label="管理者"
                          options={['ユーザーA', 'ユーザーB']}
                          required
                        />
                      </Grid>
                      <Grid size={4}>
                        <GuideMockTextField label="管理番号1" />
                      </Grid>
                      <Grid size={4}>
                        <GuideMockTextField label="管理番号2" />
                      </Grid>
                      <Grid size={4}>
                        <GuideMockTextField label="管理番号3" />
                      </Grid>
                      <Grid size={4}>
                        <GuideMockTextField label="情報1" />
                      </Grid>
                      <Grid size={4}>
                        <GuideMockTextField label="情報2" />
                      </Grid>
                      <Grid size={4}>
                        <GuideMockTextField label="情報3" />
                      </Grid>
                      <Grid size={12}>
                        <GuideMockTextField label="備考（6行表示）" />
                      </Grid>
                    </Grid>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '「登録」ボタンを押下して資産を登録する',
              content: (
                <GuideMockContainer label="フッターボタン">
                  <GuideMockButton label="登録" startIcon={<InventoryIcon />} />
                </GuideMockContainer>
              ),
            },
          ]}
        />
      </GuideSection>

      <GuideSection title="入力項目">
        <GuideTable
          headers={['項目名', '必須', '形式', '備考']}
          rows={[
            ['資産区分', '必須', 'プルダウン', 'PC / モニター / 周辺機器'],
            ['資産名', '必須', 'テキスト（最大200桁）', '資産の名称'],
            ['利用者', '必須', 'ユーザー選択', 'ユーザー一覧から選択'],
            ['管理者', '必須', 'ユーザー選択', 'ユーザー一覧から選択'],
            ['管理番号1', '任意', 'テキスト（最大50桁）', ''],
            ['管理番号2', '任意', 'テキスト（最大50桁）', ''],
            ['管理番号3', '任意', 'テキスト（最大50桁）', ''],
            ['情報1', '任意', 'テキスト（最大100桁）', ''],
            ['情報2', '任意', 'テキスト（最大100桁）', ''],
            ['情報3', '任意', 'テキスト（最大100桁）', ''],
            ['備考', '任意', 'テキストエリア（最大10000桁）', '6行表示'],
          ]}
        />
      </GuideSection>

      <GuideSection title="ボタンと操作">
        <GuideTable
          headers={['操作', '動作']}
          rows={[
            ['← 戻る矢印（タイトル左）', '登録を破棄して一覧画面に戻る'],
            ['登録ボタン', '入力内容で資産を登録する'],
          ]}
        />
      </GuideSection>

      <GuideSection title="注意事項">
        <Stack spacing={1}>
          <GuideInfoBox>資産IDは自動的に発行されます。</GuideInfoBox>
          <GuideInfoBox type="warning">
            資産区分は登録後に変更できません。慎重に選択してください。
          </GuideInfoBox>
          <GuideInfoBox>
            PCを登録した場合、次回セキュリティチェック開始時にPCパッチ確認の対象となります。
          </GuideInfoBox>
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideAssessmentRegister;
